import { z } from 'zod';

import { environmentSchema } from './config.types';

const coercableToBooleanSchema = (isTrueByDefault: boolean) =>
  z.coerce
    .string()
    .transform((v): boolean => {
      if (isTrueByDefault) {
        return v !== 'false' && v !== '0';
      }
      return v === 'true' || v === '1';
    })
    .pipe(z.coerce.boolean())
    .catch(() => isTrueByDefault);

const env = environmentSchema.parse(process.env['NEXT_PUBLIC_ENV'] ?? 'dev');
const appName = z
  .enum(['app', 'blog', 'marketing', 'oauth', 'overview'])
  .optional()
  .parse(process.env['NEXT_PUBLIC_APP_NAME']);
const buildSha = z.string().optional().parse(process.env['NEXT_PUBLIC_BUILD_SHA']);
const isTestEnv =
  process.env['CI'] === 'true' ||
  process.env['NODE_ENV'] === 'test' ||
  coercableToBooleanSchema(false).parse(process.env['NEXT_PUBLIC_E2E']);
const isMaintenanceMode = coercableToBooleanSchema(false).parse(process.env['NEXT_PUBLIC_MAINTENANCE_MODE']);
const isMSWLoggingEnabled = coercableToBooleanSchema(false).parse(process.env['NEXT_PUBLIC_MSW_LOGGING']);
const gaMeasurementId = z.string({ coerce: true }).optional().parse(process.env['NEXT_PUBLIC_GA_MEASUREMENT_ID']);
const disableNextErrorOverlay = coercableToBooleanSchema(false).parse(
  process.env['NEXT_PUBLIC_DISABLE_NEXT_ERROR_OVERLAY'],
);
const disableStaticPathGeneration = coercableToBooleanSchema(false).parse(
  process.env['NEXT_PUBLIC_DISABLE_STATIC_PATH_GENERATION'],
);
const scheduledLiquidation = coercableToBooleanSchema(false).parse(process.env['NEXT_PUBLIC_SCHEDULED_LIQUIDATION']);
const referreeNotice = z.string().optional().parse(process.env['NEXT_PUBLIC_REFERREE_NOTICE']);
const referreeTooltip = z.string().optional().parse(process.env['NEXT_PUBLIC_REFERREE_TOOLTIP']);
const referralTermsLink = z.string().optional().parse(process.env['NEXT_PUBLIC_REFERRAL_TERMS_LINK']);
const showOtcDisclaimer = coercableToBooleanSchema(false).parse(process.env['NEXT_PUBLIC_FEATURE_OTC_DISCLAIMER']);
const bypassPrivyAuth = coercableToBooleanSchema(false).parse(process.env['NEXT_PUBLIC_AUTH_BYPASS_PRIVY']);

/**
 * Parsed environment variables.
 */
export const featureFlags = {
  env,
  appName,
  buildSha,
  isTestEnv,
  isMaintenanceMode,
  isMSWLoggingEnabled,
  gaMeasurementId,
  disableNextErrorOverlay,
  disableStaticPathGeneration,
  scheduledLiquidation,
  referreeNotice,
  referreeTooltip,
  referralTermsLink,
  showOtcDisclaimer,
  bypassPrivyAuth,
};

/**
 * Server-side feature flag constants.
 * These are used for cookie-based feature flag validation.
 */
export const serverFeatureFlags = {
  // EXPERIMENTAL_COINBASE_DONATIONS: '--cb',
  // EXPERIMENTAL_ROBINHOOD_DONATIONS: '--rh',
};

// Helper function to check if a feature flag is enabled via cookie
export const isFeatureFlagEnabled = (flagValue: string): boolean => {
  if (env === 'local' || env === 'dev') return true;

  if (typeof document === 'undefined') return false;
  const cookies = document.cookie.split(';');
  const featureFlagCookie = cookies.find(c => c.trim().startsWith('NdaoFeatureFlag='));
  if (!featureFlagCookie) return false;
  const cookieValue = featureFlagCookie.split('=')[1];
  return cookieValue === flagValue;
};
